import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { articles } from "@/constants/articles";

interface ArticleContent {
  title: string;
  description: string;
}

export default function ArticlesSection() {
  const { t } = useTranslation();
  const { lang = "en" } = useParams();

  return (
    <section id="guides" className="scroll-mt-20 bg-background-100">
      <div className="mx-auto w-full max-w-6xl px-4 py-14 md:px-6 md:py-20">
        <div className="text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary-700 md:text-sm">
            {t("nav.guides")}
          </p>
          <h2 className="mt-3 text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
            {t("guides.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600 md:text-base">
            {t("guides.subtitle")}
          </p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((a) => {
            const content = t(`articles.${a.slug}`, { returnObjects: true }) as ArticleContent;
            return (
              <Link
                key={a.slug}
                to={`/${lang}/article/${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative h-40 w-full overflow-hidden">
                  <img
                    src={a.image}
                    alt={content?.title}
                    className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <div className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                      <i className={`${a.icon} text-base leading-none`} />
                    </span>
                    <span className="text-xs font-medium uppercase tracking-wide text-foreground-500">
                      {t("article.breadcrumb")}
                    </span>
                  </div>
                  <h3 className="mt-3 text-base font-semibold leading-snug text-foreground-950 md:text-lg">
                    {content?.title}
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                    {content?.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap text-sm font-semibold text-primary-700">
                    {t("guides.readMore")}
                    <i className="ri-arrow-right-line flex h-4 w-4 items-center justify-center text-base leading-none transition-transform group-hover:translate-x-0.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}